import React, { useEffect, useState, useMemo, useCallback } from 'react';

import classNames from 'classnames';

import { calculateRowTotals } from '../../utils/calculations';
import { percentFigure } from '../../utils/percentFigure';
import { getIndentClass } from '../../utils/styleUtils';

import RevenueMilestoneFields from './RevenueMilestoneFields';

import { formatFigure } from 'Helpers/formatService';

export default function UncommittedRow(props) {
    const { type, label, data, periods, indentLevel, contract, maxRevenueMilestonesCount, revenue, totalUncommittedValue = 0 } = props;

    // Memoize milestones to avoid recalculation
    const milestones = useMemo(() => {
        return data.milestones ? data.milestones : [];
    }, [data.milestones]);

    const [currentMilestones, setCurrentMilestones] = useState(milestones);

    // Row-level editing state
    const [isRowEditing, setIsRowEditing] = useState(false);
    const [editedValues, setEditedValues] = useState({});

    // Memoize committed/editable state
    const isCommitted = useMemo(() => !!contract, [contract]);
    const isEditable = useMemo(() => props.canEdit && !isCommitted, [props.canEdit, isCommitted]);

    useEffect(() => {
        // Sort milestones by date to ensure correct chronological order
        const sortedMilestones = [...milestones].sort((a, b) => new Date(a.date) - new Date(b.date));
        setCurrentMilestones(sortedMilestones);
    }, [milestones, periods]);

    const internalSetEditing = useCallback(
        (milestone_index, editType) => {
            if (!isEditable || isCommitted) return;
            props.setRevenueEditing(revenue.id, milestone_index, editType, type);
        },
        [isEditable, isCommitted, props, revenue.id, type],
    );

    const closeChangeMilestone = useCallback(() => {
        props.setRevenueEditing(null, null, null, null);
    }, [props]);

    // Respond to Edit All trigger
    useEffect(() => {
        const t = props.editAllTrigger;
        if (!t?.version) return;

        const matchesTarget = t.target === 'all' || t.target === 'revenues';
        if (!matchesTarget) return;

        setIsRowEditing(!!t.on);
    }, [props.editAllTrigger?.version]);

    const handleValueChange = useCallback((index, value) => {
        setEditedValues((prev) => ({
            ...prev,
            [index]: value,
        }));
    }, []);

    const getTextClass = (level) => {
        if (level > 2 && isCommitted) {
            return '!tw-font-bold';
        }
    };

    const totals = useMemo(() => {
        // While editing, preview totals should reflect in-progress editedValues
        if (isRowEditing && editedValues && data.currentMilestones) {
            const byYearMonth = new Map();

            currentMilestones.forEach((m) => {
                const d = new Date(m.date);
                const key = `${d.getFullYear()}-${d.getMonth()}`;
                byYearMonth.set(key, { ...m });
            });

            Object.entries(editedValues).forEach(([index, value]) => {
                const periodIndex = parseInt(index);
                const periodDate = data.currentMilestones[periodIndex]?.date;
                if (!periodDate) return;

                const d = new Date(periodDate);
                const key = `${d.getFullYear()}-${d.getMonth()}`;
                const num = parseFloat(value);

                if (!isNaN(num) && num > 0) {
                    const existing = byYearMonth.get(key) || { date: periodDate };
                    existing.percentage = num;
                    // Calculate live amount for this milestone
                    existing.amount = totalUncommittedValue > 0 ? (totalUncommittedValue * num) / 100 : 0;
                    byYearMonth.set(key, existing);
                } else if (byYearMonth.has(key)) {
                    const existing = byYearMonth.get(key);
                    existing.percentage = 0;
                    existing.amount = 0;
                    byYearMonth.set(key, existing);
                }
            });

            return calculateRowTotals(Array.from(byYearMonth.values()));
        }

        return calculateRowTotals(currentMilestones);
    }, [currentMilestones, editedValues, isRowEditing, data.currentMilestones, totalUncommittedValue]);

    return (
        <tr className="!tw-bg-white">
            <td className={getIndentClass(indentLevel)}></td>
            <td className="tw-p-4">
                <div className="tw-font-medium">{label}</div>
            </td>
            <td className={classNames('tw-p-4 tw-text-right tw-border-r-2', getTextClass(indentLevel))}>{formatFigure(totalUncommittedValue)}</td>
            {[...Array(maxRevenueMilestonesCount)].map((e, i) => {
                return (
                    <React.Fragment key={`${type}_milestone_${i}`}>
                        <RevenueMilestoneFields
                            key={`${props.keyPrefix || ''}${type}_milestone_${revenue.id}_${i}`}
                            revenue={{
                                ...revenue,
                                currentMilestones: (() => {
                                    const structured = [];
                                    if (data.currentMilestones && data.currentMilestones[i]) {
                                        const periodDate = new Date(data.currentMilestones[i].date);
                                        const milestonesInThisPeriod = currentMilestones.filter((milestone) => {
                                            const milestoneDate = new Date(milestone.date);
                                            return (
                                                milestoneDate.getFullYear() === periodDate.getFullYear() && milestoneDate.getMonth() === periodDate.getMonth()
                                            );
                                        });
                                        if (milestonesInThisPeriod.length > 0) {
                                            const milestone = milestonesInThisPeriod[0];
                                            const amount = totalUncommittedValue > 0 ? (totalUncommittedValue * (milestone.percentage || 0)) / 100 : 0;
                                            structured[i] = {
                                                ...milestone,
                                                amount: amount,
                                            };
                                        }
                                    }
                                    // Always provide the date for empty cells to enable new milestone creation
                                    if (!structured[i] && data.currentMilestones[i]) {
                                        structured[i] = {
                                            date: data.currentMilestones[i].date,
                                        };
                                    }
                                    return structured;
                                })(),
                                id: revenue.id,
                            }}
                            editingRevenueId={props.editingRevenueId}
                            editingType={props.editingType}
                            editingMilestoneId={props.editingMilestoneId}
                            editingMilestoneType={props.editingMilestoneType}
                            internalSetEditing={internalSetEditing}
                            saveMilestonePercentage={() => {}}
                            closeChangeMilestone={closeChangeMilestone}
                            milestoneId={i}
                            milestoneType={type}
                            keyPrefix={props.keyPrefix || ''}
                            canEdit={props.canEdit}
                            // Row-level editing props
                            isRowEditing={isRowEditing}
                            editedValues={editedValues}
                            handleValueChange={handleValueChange}
                            totalUncommittedValue={totalUncommittedValue}
                        />
                    </React.Fragment>
                );
            })}
            <td className="tw-p-4 tw-text-right">{percentFigure(totals.totalPercentage)}</td>
            <td className="tw-p-4 tw-text-right">{formatFigure(totals.totalValue)}</td>
            <td className="tw-p-4 tw-text-center"></td>
        </tr>
    );
}
